// Surveys 2.0 "Skip when known" (Server: engagement.survey/v2/knownContact.ts,
// keep the rules the same). A contact question (every email question, a short
// question with contactField name / phone) with skipWhenKnown (unset = on) is
// not shown to an identified contact (Gleap.identify) whose value is known.
// The widget never sends that value: it lists the question in `skippedKnown`
// and puts it on the path where it was passed, and the server writes the
// answer from the session.
//
// A skipped question is passed like an answered one: its own logic runs with
// the known value (first matching jump, else `next`, else the next block), so
// a jump that targets it continues to where it would have gone.
import type { SurveyAnswerValue, SurveyBlock, SurveyDefinition } from "./surveyTypes";

export type KnownContactField = "email" | "name" | "phone";

/** The contact property a question asks for, or null. */
export const contactFieldOf = (
  block: Pick<SurveyBlock, "type" | "contactField">,
): KnownContactField | null => {
  if (block.type === "email") return "email";
  if (
    block.type === "short" &&
    (block.contactField === "name" || block.contactField === "phone")
  ) {
    return block.contactField;
  }
  return null;
};

export const skipsWhenKnown = (
  block: Pick<SurveyBlock, "type" | "contactField" | "skipWhenKnown">,
): boolean => contactFieldOf(block) !== null && block.skipWhenKnown !== false;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const MAX_LENGTH: Record<KnownContactField, number> = { email: 320, name: 200, phone: 50 };
const NULLISH_STRINGS = new Set(["null", "undefined"]);

/**
 * The value an identified contact has for `field`, or null. Guests are asked
 * (their name can be a guess from their email), so are empty or invalid values.
 */
export const knownContactValue = (session: any, field: KnownContactField): string | null => {
  if (!session) return null;
  const userId = session.userId;
  const identified =
    typeof userId === "string" ? userId.trim().length > 0 : typeof userId === "number";
  if (!identified) return null;
  const raw = session[field];
  if (typeof raw !== "string") return null;
  const value = raw.replace(CONTROL_CHARS, "").trim();
  if (!value || value.length > MAX_LENGTH[field]) return null;
  if (field === "email" && !EMAIL_PATTERN.test(value)) return null;
  // Hosts that turn a JSON null into a string (Android getString) send "null".
  if (field !== "email" && NULLISH_STRINGS.has(value.toLowerCase())) return null;
  if (field === "name" && value.toLowerCase() === "guest") return null;
  return value;
};

/** Block id → known value, for every question this respondent skips. */
export const knownContactAnswers = (
  def: SurveyDefinition,
  session: any,
): Record<string, string> => {
  const known: Record<string, string> = {};
  for (const block of def.blocks) {
    if (!skipsWhenKnown(block)) continue;
    const value = knownContactValue(session, contactFieldOf(block)!);
    if (value === null) continue;
    known[block.id] = block.maxLength ? value.slice(0, block.maxLength) : value;
  }
  return known;
};

export type NextOf = (block: SurveyBlock, answer: SurveyAnswerValue | undefined) => string;

/**
 * From `id` past the skipped questions: the first question to show (or an
 * ending / "" when nothing is left) and the skipped ids on the way. A loop in
 * a draft (publish rejects backward jumps) stops at the question seen twice.
 */
export const passSkipped = (
  def: SurveyDefinition,
  id: string,
  known: Record<string, string>,
  next: NextOf,
): { id: string; skipped: string[] } => {
  const skipped: string[] = [];
  const seen = new Set<string>();
  let current = id;
  while (current && known[current] !== undefined && !seen.has(current)) {
    const block = def.blocks.find((b) => b.id === current);
    if (!block) break;
    seen.add(current);
    skipped.push(current);
    current = next(block, known[current]);
  }
  if (current && seen.has(current)) {
    // Looped back onto a skipped question: nothing after it can be shown.
    return { id: def.endings[0]?.id ?? "", skipped };
  }
  return { id: current, skipped };
};

/**
 * The respondent's path as the server stores it: the shown steps (and the
 * ending, if any) with the skipped questions put back where they were passed.
 * A chain counts only when it leads to the step that actually followed.
 */
export const withSkippedSteps = (
  def: SurveyDefinition,
  steps: string[],
  known: Record<string, string>,
  answers: Record<string, SurveyAnswerValue | undefined>,
  next: NextOf,
): { path: string[]; skipped: string[] } => {
  if (Object.keys(known).length === 0 || steps.length === 0) {
    return { path: steps, skipped: [] };
  }
  const path: string[] = [];
  const skipped: string[] = [];
  const firstId = def.blocks[0]?.id;
  if (firstId && firstId !== steps[0]) {
    const lead = passSkipped(def, firstId, known, next);
    if (lead.id === steps[0]) {
      path.push(...lead.skipped);
      skipped.push(...lead.skipped);
    }
  }
  steps.forEach((id, index) => {
    path.push(id);
    const following = steps[index + 1];
    const block = def.blocks.find((b) => b.id === id);
    if (!following || !block) return;
    const chain = passSkipped(def, next(block, answers[block.key]), known, next);
    if (chain.skipped.length > 0 && chain.id === following) {
      path.push(...chain.skipped);
      skipped.push(...chain.skipped);
    }
  });
  return { path, skipped };
};

/**
 * A restored path (local state, a resume reminder, the server copy with the
 * skipped questions on it) without the skipped questions. A cursor that stood
 * on one moves past it; null when nothing is left to show.
 */
export const shownSteps = (
  def: SurveyDefinition,
  path: string[],
  cursor: number,
  known: Record<string, string>,
  next: NextOf,
): { path: string[]; cursor: number } | null => {
  if (Object.keys(known).length === 0) return { path, cursor };
  const isShown = (id: string) => known[id] === undefined;
  const atId = path[cursor];
  const before = path.slice(0, cursor).filter(isShown);
  if (atId !== undefined && isShown(atId)) {
    return { path: [...before, atId, ...path.slice(cursor + 1).filter(isShown)], cursor: before.length };
  }
  const target = atId === undefined ? "" : passSkipped(def, atId, known, next).id;
  if (target && def.blocks.some((b) => b.id === target) && !before.includes(target)) {
    return { path: [...before, target], cursor: before.length };
  }
  // Everything after it is skipped: back on the last shown question (answering it again finishes).
  if (before.length > 0) return { path: before, cursor: before.length - 1 };
  return null;
};
